import type { TabKey } from "@libertify/experience-viewer";
import { CONFIG_ITEM } from "pages/create-experience/data/addons";
import type { ConfigItemId } from "pages/create-experience/components/config-cards/registry";

/**
 * Knowledge Hub tab -> Edit page rail item, for every add-on in MED-7371's
 * scope: Video, Key points (Summary), Quick recall (Flashcards), Quiz,
 * Podcast, Standouts — i.e. every entry in `TAB_ORDER`. Chatbot has no tab
 * (it's a side panel, not a Knowledge Hub tab) and `audioDoc` has none
 * visible either (MED-7032), so neither needs an entry here.
 *
 * Starts empty — each add-on's own subtask (MED-7373..7378) adds its entry
 * once that add-on's "Edit" CTA is wired, so this map and the tab strip's
 * actual CTA coverage can never drift apart mid-rollout.
 */
const TAB_TO_CONFIG_ITEM: Partial<Record<TabKey, ConfigItemId>> = {
  video: CONFIG_ITEM.Video,
  summary: CONFIG_ITEM.Summary,
  flashcards: CONFIG_ITEM.Flashcards,
  quiz: CONFIG_ITEM.Quiz,
  podcast: CONFIG_ITEM.Podcast,
  standouts: CONFIG_ITEM.Standouts,
};

export type EditPageNavState = {
  tab: ConfigItemId;
};

/**
 * The Edit page's path for a given project/instance — one URL regardless of
 * which card opens (see `getEditNavState`). Shared so the page header's own
 * generic "Edit" CTA and each add-on's own "Edit {addon}" CTA build the exact
 * same path instead of two copies of this template drifting apart.
 */
export const getEditPagePath = (
  projectId: string | undefined,
  instanceId: string
): string => `/media/projects/${projectId}/experience-v2/${instanceId}/edit`;

/**
 * Router-state payload for an add-on's own "Edit {addon}" link (rendered
 * inside that tab's content, below the tab strip — the page header's own
 * "Edit" CTA always opens on General and never carries this):
 * `navigate(editPath, getEditNavState(tab))`.
 * Deliberately not a URL param — the Edit page has one URL regardless of which
 * card opens, so Back leaves the page instead of stepping through cards, and
 * nothing has to keep a param and the rail's local state in sync. A refresh on
 * the Edit page loses this and opens General, same as today.
 */
export const getEditNavState = (
  tab: TabKey | undefined
): { state: EditPageNavState } | undefined => {
  const configItem = tab ? TAB_TO_CONFIG_ITEM[tab] : undefined;
  return configItem ? { state: { tab: configItem } } : undefined;
};

/**
 * Reads the `tab` the Edit CTA may have passed via `getEditNavState`, from
 * `useLocation().state`. Narrows rather than casting: `location.state` can
 * hold anything a previous `navigate` call put there (or nothing, on a direct
 * visit or a refresh), so it isn't trusted to already be an `EditPageNavState`.
 */
export const readEditNavTab = (state: unknown): ConfigItemId | undefined => {
  if (typeof state !== "object" || state === null || !("tab" in state)) {
    return undefined;
  }
  const { tab } = state as { tab: unknown };
  return typeof tab === "string" ? (tab as ConfigItemId) : undefined;
};
